import { FIELD_FORMAT, fieldUnit } from '../constants/field-format'
import {
  FLOOR_TYPE_LABELS,
  FLOOR_TYPE_PLURAL_LABELS,
} from '../locales/de/enum-labels'
import { FIELD_LABELS } from '../locales/de/fields'
import { TERMS } from '../locales/de/terms'
import {
  EnergyLabel,
  FloorType,
  PropertyType,
  RoomType,
  SubType,
  type Unit,
} from '@prisma/client'

import {
  buildFloorPositions,
  countFloorsOfType,
  floorFormulaFields,
  type FloorForPosition,
} from './floor-position'
import { formulaNames, formulaReferences } from './formula-tokens'
import { math } from './math-instance'
import type { SelectionScope } from './selection-scope'

const PROPERTY_TYPES = Object.values(PropertyType) as string[]
const SUB_TYPES = Object.values(SubType) as string[]
const ENERGY_LABELS = Object.values(EnergyLabel) as string[]
const ROOM_TYPES = Object.values(RoomType) as string[]
const FLOOR_TYPES = Object.values(FloorType) as string[]

export type PropertyForFormula = {
  type?: string | null
  subType?: string | null
  livingArea?: number | null
  landArea?: number | null
  numberOfRooms?: number | null
  numberOfFloors?: number | null
  gardenArea?: number | null
  balconyArea?: number | null
  numberOfBathrooms?: number | null
  numberOfIndoorParkingSpaces?: number | null
  numberOfOutdoorParkingSpaces?: number | null
  floorNumber?: number | null
  constructionYear?: number | null
  renovationYear?: number | null
  marketPrice?: number | null
  potentialPrice?: number | null
  energyConsumption?: number | null
  energyLabel?: string | null
  isNew?: boolean | null
  leasehold?: boolean | null
}

export type FormulaVariableKey =
  | keyof PropertyForFormula
  | RoomVariableKey
  | FloorVariableKey

type NumericKey = Extract<
  FormulaVariableKey,
  | 'livingArea'
  | 'landArea'
  | 'numberOfRooms'
  | 'numberOfFloors'
  | 'gardenArea'
  | 'balconyArea'
  | 'numberOfBathrooms'
  | 'numberOfIndoorParkingSpaces'
  | 'numberOfOutdoorParkingSpaces'
  | 'floorNumber'
  | 'constructionYear'
  | 'renovationYear'
  | 'marketPrice'
  | 'potentialPrice'
  | 'energyConsumption'
>

type BooleanKey = Extract<FormulaVariableKey, 'isNew' | 'leasehold'>

type PropertyVarSpec = {
  key: FormulaVariableKey
  label: string
  unit: string
  /** The unit behind that symbol, where the field declares one. What decides
   *  whether a rate can be counted in this figure — the symbol is how it is
   *  printed, not what it is. */
  measuredIn?: Unit | null
  /** A figure the project counts: rooms, storeys, parking spaces. It carries
   *  no unit because a count names what it counts, which is why it cannot be
   *  told from a year or a yes/no by the absence of one. */
  counts?: boolean
  mode: 'QUANTITY' | 'OPTION'
  options?: readonly string[]
  read: (p: PropertyForFormula) => number | string | null
}

const numericSpec = (key: NumericKey): PropertyVarSpec => ({
  key,
  label: FIELD_LABELS[key].label,
  unit: fieldUnit(key),
  measuredIn: FIELD_FORMAT[key]?.unit ?? null,
  mode: 'QUANTITY',
  read: p => (p[key] as number | null | undefined) ?? null,
})

/** A numeric field that counts things rather than measuring them. */
const countSpec = (key: NumericKey): PropertyVarSpec => ({
  ...numericSpec(key),
  counts: true,
})

const booleanSpec = (key: BooleanKey): PropertyVarSpec => ({
  key,
  label: FIELD_LABELS[key].label,
  unit: '',
  mode: 'QUANTITY',
  read: p => {
    const v = p[key]
    return v == null ? null : v ? 1 : 0
  },
})

const PROPERTY_VAR_SPECS: readonly PropertyVarSpec[] = [
  // Areas
  numericSpec('livingArea'),
  numericSpec('landArea'),
  numericSpec('gardenArea'),
  numericSpec('balconyArea'),
  // Counts
  countSpec('numberOfRooms'),
  countSpec('numberOfFloors'),
  countSpec('numberOfBathrooms'),
  countSpec('numberOfIndoorParkingSpaces'),
  countSpec('numberOfOutdoorParkingSpaces'),
  countSpec('floorNumber'),
  // Years
  numericSpec('constructionYear'),
  numericSpec('renovationYear'),
  // Prices
  numericSpec('marketPrice'),
  numericSpec('potentialPrice'),
  // Energy
  numericSpec('energyConsumption'),
  {
    key: 'energyLabel',
    label: FIELD_LABELS.energyLabel.label,
    unit: '',
    mode: 'OPTION',
    options: ENERGY_LABELS,
    read: p => p.energyLabel ?? null,
  },
  // Booleans as 0/1
  booleanSpec('isNew'),
  booleanSpec('leasehold'),
  // String enums
  {
    key: 'type',
    label: FIELD_LABELS.type.label,
    unit: '',
    mode: 'OPTION',
    options: PROPERTY_TYPES,
    read: p => p.type ?? null,
  },
  {
    key: 'subType',
    label: FIELD_LABELS.subType.label,
    unit: '',
    mode: 'OPTION',
    options: SUB_TYPES,
    read: p => p.subType ?? null,
  },
]

// ─── Room-scope variables ──────────────────────────────────────────────
//
// Injected additionally into the formula scope when a formula is being
// evaluated for a specific room (per-room prices, per-room availability
// gates). The `room` prefix disambiguates from Property-scope keys
// (`type` vs `roomType`) and reserves the whole `room…` namespace against
// admin-defined HouseElementType keys.
//
// Evaluation without a focus room covers a set of rooms — the property, or
// one storey: area-like sums aggregate over that set; single-room dimensions
// (length/width/height) and `roomType` become null so `isset(roomLength)`
// returns 0 — matches the existing pattern that PER_ROOM+OPTION HouseElement
// vars are absent from property-level scope. The `roomFloor…` keys answer for
// the storey the set shares, and are null for the property, which shares none.

/**
 * Every column of a room a formula can read.
 *
 * Held against `RoomForFormula` below, which is what the scope builder takes,
 * so the two cannot disagree. A writer of a room reads it to ask which of the
 * columns it is about to write a price can reach — the variable names cannot
 * answer that, because one is `roomArea` where the column is `area`.
 *
 * `floorType` and `floorLevel` are the storey's, projected onto the room.
 */
export const ROOM_FORMULA_COLUMNS = [
  'length',
  'width',
  'height',
  'perimeter',
  'area',
  'wallArea',
  'roomType',
  'floorType',
  'floorLevel',
] as const satisfies readonly (keyof RoomForFormula)[]

export type RoomForFormula = {
  length?: number | null
  width?: number | null
  height?: number | null
  perimeter?: number | null
  area?: number | null
  wallArea?: number | null
  roomType?: string | null
  floorType?: string | null
  floorLevel?: number | null
}

export type RoomVariableKey =
  | 'roomLength'
  | 'roomWidth'
  | 'roomHeight'
  | 'roomPerimeter'
  | 'roomArea'
  | 'roomWallArea'
  | 'roomType'
  | 'roomFloor'
  | 'roomFloorLevel'
  | 'roomFloorHeightMax'
  | 'roomFloorAreaSum'
  | 'roomFloorWallAreaSum'
  | 'roomFloorPerimeterSum'
  | 'roomFloorNumberOfRooms'

type RoomVarSpec = {
  key: RoomVariableKey
  label: string
  unit: string
  measuredIn?: Unit | null
  counts?: boolean
  mode: 'QUANTITY' | 'OPTION'
  options?: readonly string[]
  /** `floorRooms` are the rooms sharing the focus room's storey, the focus
   *  room included — empty when its storey is unknown. */
  read: (
    r: RoomForFormula,
    floorRooms: readonly RoomForFormula[],
  ) => number | string | null
  /**
   * Value for a set of rooms rather than one — the whole property, or one
   * storey. `storey` is given when the set is a storey, which is what makes
   * the `roomFloor…` keys answerable there. Returns null for fields that
   * don't aggregate meaningfully (length/width/height/roomType).
   */
  resolve: (
    rooms: readonly RoomForFormula[],
    storey: StoreyForFormula | null,
  ) => number | string | null
  description: string
}

/** Sum non-null `pick(room)` across `rooms`. Returns null when no room
 *  has a value (so `isset(roomArea)` reports 0 for an empty property).
 *  NaN/Infinity are also skipped — `NaN == null` is false, so without an
 *  explicit `isFinite` check a rogue NaN would poison the accumulator
 *  and the function would return NaN, which the outer null-coercion
 *  can't distinguish from "no data". */
export function sumOrNull<R>(
  rooms: readonly R[],
  pick: (r: R) => number | null | undefined,
): number | null {
  let sum = 0
  let seen = false
  for (const r of rooms) {
    const v = pick(r)
    if (v == null || !Number.isFinite(v)) continue
    sum += v
    seen = true
  }
  return seen ? sum : null
}

/** Largest non-null `pick(room)`, or null when no room carries one. */
function maxOrNull(
  rooms: readonly RoomForFormula[],
  pick: (r: RoomForFormula) => number | null | undefined,
): number | null {
  let max: number | null = null
  for (const r of rooms) {
    const v = pick(r)
    if (v == null || !Number.isFinite(v)) continue
    if (max === null || v > max) max = v
  }
  return max
}

/** A per-storey sum. Mirrors `roomNumericSum`, but over the focus room's
 *  storey rather than the property. */
const roomFloorSum = (
  key: Extract<
    RoomVariableKey,
    'roomFloorAreaSum' | 'roomFloorWallAreaSum' | 'roomFloorPerimeterSum'
  >,
  prop: 'area' | 'wallArea' | 'perimeter',
  description: string,
): RoomVarSpec => ({
  key,
  label: `${FIELD_LABELS[prop].label} im Geschoss`,
  unit: fieldUnit(prop),
  mode: 'QUANTITY',
  read: (_, floorRooms) => sumOrNull(floorRooms, r => r[prop]),
  resolve: (rooms, storey) => (storey ? sumOrNull(rooms, r => r[prop]) : null),
  description,
})

// A narrowed variable is the property-scope field seen through something —
// one room, one storey type — so it is named after that field. A hyphenated
// compound takes no article and no adjective, which is what makes the slot
// safe to fill.
const compoundLabel = (prefix: string, prop: keyof typeof FIELD_LABELS) =>
  `${prefix}-${FIELD_LABELS[prop].label}`

const roomVarLabel = (prop: keyof typeof FIELD_LABELS) =>
  compoundLabel(TERMS.room, prop)

const roomDimensionSingle = (
  key: Extract<RoomVariableKey, 'roomLength' | 'roomWidth' | 'roomHeight'>,
  prop: 'length' | 'width' | 'height',
  description: string,
): RoomVarSpec => ({
  key,
  label: roomVarLabel(prop),
  unit: fieldUnit(prop),
  measuredIn: FIELD_FORMAT[prop]?.unit ?? null,
  mode: 'QUANTITY',
  read: r => r[prop] ?? null,
  // Length/width/height don't sum meaningfully. A formula that reads one of
  // them outside a room short-circuits unless guarded by isset().
  resolve: () => null,
  description,
})

// Used for area, wallArea, AND perimeter — anything whose SUM across all
// rooms is meaningful. Name kept intentionally generic ("numeric sum")
// so `roomPerimeter` (a length, not an area) reads naturally.
const roomNumericSum = (
  key: Extract<RoomVariableKey, 'roomArea' | 'roomWallArea' | 'roomPerimeter'>,
  prop: 'area' | 'wallArea' | 'perimeter',
  description: string,
): RoomVarSpec => ({
  key,
  label: roomVarLabel(prop),
  unit: fieldUnit(prop),
  measuredIn: FIELD_FORMAT[prop]?.unit ?? null,
  mode: 'QUANTITY',
  read: r => r[prop] ?? null,
  resolve: rooms => sumOrNull(rooms, r => r[prop] ?? null),
  description,
})

const ROOM_VAR_SPECS: readonly RoomVarSpec[] = [
  roomDimensionSingle(
    'roomLength',
    'length',
    'Länge des aktuellen Raums in m. Property-Kontext: null (nicht summierbar).',
  ),
  roomDimensionSingle(
    'roomWidth',
    'width',
    'Breite des aktuellen Raums in m. Property-Kontext: null.',
  ),
  roomDimensionSingle(
    'roomHeight',
    'height',
    'Höhe des aktuellen Raums in m. Property-Kontext: null.',
  ),
  roomNumericSum(
    'roomArea',
    'area',
    'Bodenfläche des aktuellen Raums in m². Geschoss-Kontext: Summe über die Räume des Geschosses. Property-Kontext: Summe über alle Räume.',
  ),
  roomNumericSum(
    'roomWallArea',
    'wallArea',
    'Wandfläche des aktuellen Raums in m². Geschoss-Kontext: Summe über die Räume des Geschosses. Property-Kontext: Summe über alle Räume.',
  ),
  roomNumericSum(
    'roomPerimeter',
    'perimeter',
    'Umlaufender Wandanschluss des aktuellen Raums in m. Geschoss-Kontext: Summe über die Räume des Geschosses. Property-Kontext: Summe über alle Räume.',
  ),
  {
    key: 'roomType',
    label: 'Raum-Typ',
    unit: '',
    mode: 'OPTION',
    options: ROOM_TYPES,
    read: r => r.roomType ?? null,
    resolve: () => null,
    description: `z.B. if(roomType == '${ROOM_TYPES[0] ?? ''}', a, b). Ohne Raum-Kontext: null.`,
  },
  {
    key: 'roomFloor',
    label: 'Raum-Geschosstyp',
    unit: '',
    mode: 'OPTION',
    options: FLOOR_TYPES,
    read: r => r.floorType ?? null,
    resolve: (_, storey) => storey?.floorType ?? null,
    description: `z.B. if(roomFloor == 'BASEMENT', a, b). Geschoss-Kontext: der Typ des Geschosses. Property-Kontext: null.`,
  },
  {
    key: 'roomFloorLevel',
    label: 'Raum-Geschossebene',
    unit: '',
    mode: 'QUANTITY',
    // Signed storey number: ground floor 0, upper floors counting up,
    // basements counting down (-1, -2). See `buildFloorPositions`.
    read: r => r.floorLevel ?? null,
    resolve: (_, storey) => storey?.floorLevel ?? null,
    description:
      'Geschossebene des aktuellen Raums (Erdgeschoss 0, OG aufsteigend, UG negativ). Geschoss-Kontext: die Ebene des Geschosses. Property-Kontext: null.',
  },
  {
    key: 'roomFloorHeightMax',
    label: 'Größte Raumhöhe im Geschoss',
    unit: fieldUnit('height'),
    mode: 'QUANTITY',
    read: (_, floorRooms) => maxOrNull(floorRooms, r => r.height),
    resolve: (rooms, storey) =>
      storey ? maxOrNull(rooms, r => r.height) : null,
    description:
      'Größte Raumhöhe unter den Räumen im Geschoss des aktuellen Raums, in m. Geschoss-Kontext: die größte Höhe in diesem Geschoss. Property-Kontext: null.',
  },
  roomFloorSum(
    'roomFloorAreaSum',
    'area',
    'Summe der Raumflächen im Geschoss des aktuellen Raums, in m². Geschoss-Kontext: die Summe über dieses Geschoss. Property-Kontext: null.',
  ),
  roomFloorSum(
    'roomFloorWallAreaSum',
    'wallArea',
    'Summe der Wandflächen im Geschoss des aktuellen Raums, in m². Geschoss-Kontext: die Summe über dieses Geschoss. Property-Kontext: null.',
  ),
  roomFloorSum(
    'roomFloorPerimeterSum',
    'perimeter',
    'Summe der umlaufenden Wandanschlüsse im Geschoss des aktuellen Raums, in m. Geschoss-Kontext: die Summe über dieses Geschoss. Property-Kontext: null.',
  ),
  {
    key: 'roomFloorNumberOfRooms',
    label: 'Räume im Geschoss',
    unit: '',
    mode: 'QUANTITY',
    // Zero rooms means the focus room carries no storey, not an empty floor —
    // it always counts itself when its own level is known.
    read: (_, floorRooms) => (floorRooms.length > 0 ? floorRooms.length : null),
    resolve: (rooms, storey) =>
      storey && rooms.length > 0 ? rooms.length : null,
    description:
      'Anzahl der Räume im Geschoss des aktuellen Raums. Geschoss-Kontext: die Anzahl in diesem Geschoss. Property-Kontext: null.',
  },
]

// ─── Floor-scope variables ─────────────────────────────────────────────
//
// Building-wide storey facts, available in every context. `roomFloor` covers
// the one storey a place stands on and resolves to null at the property, so it
// cannot answer "does this object have a basement?".

export type FloorForFormula = { type: FloorType }

export type FloorVariableKey =
  | 'hasBasement'
  | 'basementCount'
  | 'fullFloorCount'
  | 'atticCount'
  | 'basementAreaSum'
  | 'fullFloorAreaSum'
  | 'atticAreaSum'

type FloorVarSpec = {
  key: FloorVariableKey
  label: string
  description: string
  /** Blank for a count, which names its own unit. */
  unit?: string
  measuredIn?: Unit | null
  /** A figure that counts storeys rather than measuring them. */
  counts?: boolean
  /** `rooms` is the property's complete list: a storey's area is only
   *  recorded on the rooms standing in it. */
  read: (
    floors: readonly FloorForFormula[],
    rooms: readonly RoomForFormula[],
  ) => number | null
}

/**
 * Floor area by storey type, summed over the rooms standing in it.
 *
 * `roomFloorAreaSum` answers the neighbouring question — the storey the place
 * stands on — and is null at the property, so it cannot price a measure that
 * covers the whole building.
 *
 * There is no per-storey variant: a formula has no way to name "the second
 * full floor", and `fullFloorAreaSum / fullFloorCount` gives the average.
 *
 * A property with no storey of the type has no such area to insulate or
 * plaster, so it reads 0 — the same answer `atticCount` gives, and priceable.
 * Null is kept for the storey that exists whose rooms carry no area: that is
 * an unanswered question, and a null reaches the reader as "Auf Anfrage"
 * rather than as a free measure.
 *
 * A room whose storey is unassigned (`floorId` null, its own state in
 * Planning) stands in no storey and joins no sum.
 */
const floorTypeAreaSum = (
  key: Extract<
    FloorVariableKey,
    'basementAreaSum' | 'fullFloorAreaSum' | 'atticAreaSum'
  >,
  floorType: FloorType,
  description: string,
): FloorVarSpec => ({
  key,
  label: compoundLabel(FLOOR_TYPE_LABELS[floorType], 'area'),
  unit: fieldUnit('area'),
  measuredIn: FIELD_FORMAT.area?.unit ?? null,
  description,
  read: (floors, rooms) =>
    countFloorsOfType(floors, floorType) === 0
      ? 0
      : sumOrNull(rooms, r => (r.floorType === floorType ? r.area : null)),
})

const FLOOR_VAR_SPECS: readonly FloorVarSpec[] = [
  {
    key: 'hasBasement',
    label: `${FLOOR_TYPE_LABELS.BASEMENT} vorhanden`,
    description: `1 wenn mindestens ein ${FLOOR_TYPE_LABELS.BASEMENT} erfasst ist, sonst 0. Ohne erfasste Geschosse: null.`,
    read: floors => (countFloorsOfType(floors, 'BASEMENT') > 0 ? 1 : 0),
  },
  {
    key: 'basementCount',
    counts: true,
    label: FLOOR_TYPE_PLURAL_LABELS.BASEMENT,
    description: `Anzahl der ${FLOOR_TYPE_PLURAL_LABELS.BASEMENT}. Ohne erfasste Geschosse: null.`,
    read: floors => countFloorsOfType(floors, 'BASEMENT'),
  },
  {
    key: 'fullFloorCount',
    counts: true,
    // Counted from the FULL_FLOOR storeys in Planning, which is a different
    // number from the `numberOfFloors` recorded on the property —
    // FullFloorMismatchBadge exists to flag when the two disagree.
    label: `${FLOOR_TYPE_PLURAL_LABELS.FULL_FLOOR} (gezählt)`,
    description: `Anzahl der ${FLOOR_TYPE_PLURAL_LABELS.FULL_FLOOR} inklusive Erdgeschoss. Ohne erfasste Geschosse: null.`,
    read: floors => countFloorsOfType(floors, 'FULL_FLOOR'),
  },
  {
    key: 'atticCount',
    counts: true,
    label: FLOOR_TYPE_PLURAL_LABELS.ATTIC,
    description: `Anzahl der ${FLOOR_TYPE_PLURAL_LABELS.ATTIC}. Ohne erfasste Geschosse: null.`,
    read: floors => countFloorsOfType(floors, 'ATTIC'),
  },
  floorTypeAreaSum(
    'basementAreaSum',
    'BASEMENT',
    'Summe der Raumflächen in allen Untergeschossen, in m². Ohne Untergeschoss: 0. Mit Untergeschoss, aber ohne vermessene Räume darin: null. Räume ohne zugewiesenes Geschoss zählen nicht mit.',
  ),
  floorTypeAreaSum(
    'fullFloorAreaSum',
    'FULL_FLOOR',
    'Summe der Raumflächen in allen Vollgeschossen inklusive Erdgeschoss, in m². Ohne Vollgeschoss: 0. Mit Vollgeschoss, aber ohne vermessene Räume darin: null. Räume ohne zugewiesenes Geschoss zählen nicht mit.',
  ),
  floorTypeAreaSum(
    'atticAreaSum',
    'ATTIC',
    'Summe der Raumflächen in allen Dachgeschossen, in m². Ohne Dachgeschoss: 0. Mit Dachgeschoss, aber ohne vermessene Räume darin: null. Räume ohne zugewiesenes Geschoss zählen nicht mit.',
  ),
]

/** The three sets `FORMULA_VARIABLE_GROUPS` is built from. */
export type FormulaVariableScope = 'PROPERTY' | 'ROOM' | 'FLOOR'

/**
 * What a name inside a formula refers to. The three variable scopes are fixed
 * by the language; the rest are contributed at runtime — by the admin's
 * element types, the configurator's option codes and the formula library.
 *
 * A reader looking a name up asks "what does the object give me?" before they
 * ask for a spelling, so the editor's completion popup and its reference panel
 * are both grouped by this.
 */
export type FormulaNameKind =
  | FormulaVariableScope
  | 'ELEMENT_TYPE'
  | 'OPTION_CODE'
  | 'LIBRARY'
  | 'FUNCTION'
  /** A subtotal of the staged price the expression sits in — `subtotal` and
   *  `stage_1`, `stage_2`, … See `shared/constants/stage-variables.ts`. */
  | 'STAGE'

export type FormulaVariableMeta = {
  label: string
  description: string
  /** The symbol the figure is printed with. */
  unit: string
  /** The unit behind it, where there is one, and whether the figure counts
   *  things rather than measuring them — what a surface offering figures for
   *  a rate quoted per something has to match on. */
  measuredIn?: Unit | null
  counts?: boolean
  mode: 'QUANTITY' | 'OPTION'
  options?: readonly string[]
}

const propertyVariableMeta = (
  s: (typeof PROPERTY_VAR_SPECS)[number],
): FormulaVariableMeta => ({
  label: s.label,
  description:
    s.mode === 'QUANTITY'
      ? s.unit
        ? `${s.label} in ${s.unit}`
        : s.label
      : `z.B. if(${s.key} == '${s.options?.[0] ?? ''}', a, b)`,
  unit: s.unit,
  measuredIn: s.measuredIn ?? null,
  counts: s.counts,
  mode: s.mode,
  options: s.options,
})

const roomVariableMeta = (s: RoomVarSpec): FormulaVariableMeta => ({
  label: s.label,
  description: s.description,
  unit: s.unit,
  measuredIn: s.measuredIn ?? null,
  counts: s.counts,
  mode: s.mode,
  options: s.options,
})

const floorVariableMeta = (s: FloorVarSpec): FormulaVariableMeta => ({
  label: s.label,
  description: s.description,
  unit: s.unit ?? '',
  measuredIn: s.measuredIn ?? null,
  counts: s.counts,
  mode: 'QUANTITY',
})

/**
 * The variables the language defines, by where their value comes from.
 *
 * The grouping is which spec list a variable was declared in, so a surface
 * that lists them by scope reads it off the structure rather than off a field
 * each record restates.
 */
export const FORMULA_VARIABLE_GROUPS: Record<
  FormulaVariableScope,
  Record<string, FormulaVariableMeta>
> = {
  PROPERTY: Object.fromEntries(
    PROPERTY_VAR_SPECS.map(s => [s.key, propertyVariableMeta(s)]),
  ),
  ROOM: Object.fromEntries(
    ROOM_VAR_SPECS.map(s => [s.key, roomVariableMeta(s)]),
  ),
  FLOOR: Object.fromEntries(
    FLOOR_VAR_SPECS.map(s => [s.key, floorVariableMeta(s)]),
  ),
}

/**
 * Every column of a property a formula can read, as a runtime list.
 *
 * Derived and not stated: each `PROPERTY_VAR_SPECS` entry reads `p[key]`, so
 * the variable's name and the column's are the same word and the two cannot
 * diverge. A writer of a property asks this which of the columns it is about
 * to write a price can read.
 */
export const PROPERTY_FORMULA_COLUMNS = Object.keys(
  FORMULA_VARIABLE_GROUPS.PROPERTY,
)

export const FORMULA_VARIABLES = {
  ...FORMULA_VARIABLE_GROUPS.PROPERTY,
  ...FORMULA_VARIABLE_GROUPS.ROOM,
  ...FORMULA_VARIABLE_GROUPS.FLOOR,
} as Record<FormulaVariableKey, FormulaVariableMeta>

/**
 * Variables in scope for a formula. Keys are open-ended because HouseElement
 * types contribute admin-defined keys at runtime. QUANTITY-mode types
 * contribute numbers, OPTION-mode types contribute the selected option key
 * as a string (used in comparisons like `myType == 'WOOD'`).
 */
export type FormulaVariables = Record<string, number | string | null>

export function buildPropertyVariables(
  property: PropertyForFormula,
): FormulaVariables {
  return Object.fromEntries(
    PROPERTY_VAR_SPECS.map(s => [s.key, s.read(property)]),
  )
}

/**
 * One storey as a formula focus reads it: what it is, and which rooms stand on
 * it. A storey records no dimension of its own — its area is the area of those
 * rooms.
 */
export type StoreyForFormula = {
  floorType: string | null
  floorLevel: number | null
  rooms: RoomForFormula[]
  /** Which rooms' house-element rows the storey's sums cover. */
  roomIds: Set<number>
}

/**
 * Room-scope variables for the formula context.
 *
 * - `room != null` — per-room evaluation. Each spec's `read` fires against
 *   this specific room; missing values become `null`.
 * - `room == null` — evaluation for a set of rooms: the whole property, or one
 *   storey when `storey` names it. Each spec's `resolve` fires:
 *   area/wallArea/perimeter sum over the set (null if no room has a value);
 *   dimensions and `roomType` resolve to `null` because they don't aggregate
 *   meaningfully, and the `roomFloor…` keys answer only for a storey.
 *
 * Always injected into scope so a formula referencing a room var never
 * gets `undefined` — either a value, a sum, or `null` (which the
 * evaluator's null-guard handles via `isset(...)`).
 */
export function buildRoomVariables(
  room: RoomForFormula | null,
  allRooms: readonly RoomForFormula[],
  storey: StoreyForFormula | null = null,
): FormulaVariables {
  if (room != null) {
    // Grouped by level, not by type: two basements share a `floorType` but are
    // different storeys, and level is what positions a room uniquely.
    const floorRooms =
      room.floorLevel == null
        ? []
        : allRooms.filter(r => r.floorLevel === room.floorLevel)
    return Object.fromEntries(
      ROOM_VAR_SPECS.map(s => [s.key, s.read(room, floorRooms)]),
    )
  }
  return Object.fromEntries(
    ROOM_VAR_SPECS.map(s => [s.key, s.resolve(allRooms, storey)]),
  )
}

/**
 * Room variables for the place a formula is evaluated at — which is why
 * callers should reach for this rather than `buildRoomVariables` directly.
 *
 * The property is the aggregate over every room. A storey is the aggregate
 * over the rooms standing on it, so a measure asked once per storey is priced
 * against that storey and not against the building. A room reads its own
 * fields plus its storey's.
 *
 * A room or storey the property no longer has reads as empty: it must NOT
 * fall through to the property aggregate, or a formula like `roomArea * 25`
 * silently prices the whole building for a place that is gone.
 */
export function buildRoomVariablesForFocus(
  focus: SelectionScope,
  places: RoomsForFormula,
): FormulaVariables {
  if (focus.level === 'OBJECT') {
    return buildRoomVariables(null, places.roomsForFormula)
  }
  if (focus.level === 'FLOOR') {
    const storey = storeyIn(places, focus.floorId)
    return buildRoomVariables(null, storey.rooms, storey)
  }
  const focusRoom = places.roomById.get(focus.roomId)
  return focusRoom
    ? buildRoomVariables(focusRoom, places.roomsForFormula)
    : buildRoomVariables(null, NO_ROOMS)
}

const NO_ROOMS: readonly RoomForFormula[] = []

const EMPTY_STOREY: StoreyForFormula = {
  floorType: null,
  floorLevel: null,
  rooms: [],
  roomIds: new Set(),
}

/** A storey the property no longer has covers no room: it reads empty rather
 *  than falling through to the building. */
const storeyIn = (places: RoomsForFormula, floorId: number): StoreyForFormula =>
  places.storeyById.get(floorId) ?? EMPTY_STOREY

/** The rooms a storey focus covers, or null for a place that is not a storey
 *  — what narrows a sum from the building to one of its storeys. */
export function storeyOfFocus(
  focus: SelectionScope,
  places: RoomsForFormula,
): StoreyForFormula | null {
  return focus.level === 'FLOOR' ? storeyIn(places, focus.floorId) : null
}

/** The scope a formula context carries before a property has loaded. */
export const EMPTY_FORMULA_SCOPE: FormulaScope = {
  variables: {},
  explicitlyOverriddenNames: new Set<string>(),
}

/**
 * Floor-scope variables for the formula context. Both lists are the
 * property's complete ones — these variables are building-wide, so a partial
 * list would understate them.
 *
 * `rooms` because a storey records no area of its own; the area of a
 * basement is the area of the rooms standing in it.
 *
 * An empty storey list yields all-null values: with no storey recorded, "has
 * no basement" is unknown rather than established, and the evaluator's
 * null-guard surfaces that as an incomplete formula.
 */
export function buildFloorVariables(
  floors: readonly FloorForFormula[],
  rooms: readonly RoomForFormula[],
): FormulaVariables {
  return Object.fromEntries(
    FLOOR_VAR_SPECS.map(s => [
      s.key,
      floors.length === 0 ? null : s.read(floors, rooms),
    ]),
  )
}

/**
 * Narrow any wire-shape room object down to the `RoomForFormula` fields
 * that `buildRoomVariables` reads. Kept here (alongside the type it
 * projects into) so both configurator hooks share the same projection —
 * adding a new dimensional field is a one-file change.
 */
export function toRoomForFormula(r: RoomForFormula): RoomForFormula {
  return Object.fromEntries(
    ROOM_FORMULA_COLUMNS.map(column => [column, r[column] ?? null]),
  ) as RoomForFormula
}

/** Every place a formula can be evaluated at, projected out of one property.
 *  All three structures reference the SAME projected rooms, so adding a
 *  room-derived variable is a one-place change. */
export type RoomsForFormula = {
  /** The flat list the property-level aggregates read. */
  roomsForFormula: RoomForFormula[]
  roomById: Map<number, RoomForFormula>
  /** Keyed by floor id, and holding an entry for a storey with no rooms — an
   *  empty storey sums to null, which is not the same answer as a storey the
   *  property does not have. */
  storeyById: Map<number, StoreyForFormula>
}

/** The shape `buildRoomsForFormula` projects a property from — its floors and
 *  its rooms, as every wire format carries them. */
export type PropertyRoomsForFormula = {
  floors?: ReadonlyArray<FloorForPosition> | null
  rooms?: ReadonlyArray<
    Parameters<typeof toRoomForFormula>[0] & {
      id: number
      floorId?: number | null
    }
  > | null
}

/**
 * Projects a property's rooms into the structures every formula-context hook
 * needs: the flat list for property-level aggregates, a by-id lookup for the
 * focused-room case, and one entry per storey for a focus standing on it.
 */
export function buildRoomsForFormula(
  property: PropertyRoomsForFormula,
): RoomsForFormula {
  const roomsForFormula: RoomForFormula[] = []
  const roomById = new Map<number, RoomForFormula>()
  const storeyById = new Map<number, StoreyForFormula>()
  const positions = buildFloorPositions(property.floors ?? [])
  for (const floor of property.floors ?? []) {
    const position = positions.get(floor.id)
    storeyById.set(floor.id, {
      floorType: position?.type ?? null,
      floorLevel: position?.level ?? null,
      rooms: [],
      roomIds: new Set(),
    })
  }
  for (const room of property.rooms ?? []) {
    const projected = toRoomForFormula({
      ...room,
      ...floorFormulaFields(room.floorId, positions),
    })
    roomsForFormula.push(projected)
    roomById.set(room.id, projected)
    // A room Planning has not hung on a storey stands on none, and joins no
    // storey's sum — the same rule the floor-type area sums follow.
    const storey =
      room.floorId == null ? undefined : storeyById.get(room.floorId)
    if (storey) {
      storey.rooms.push(projected)
      storey.roomIds.add(room.id)
    }
  }
  return { roomsForFormula, roomById, storeyById }
}

export type FormulaContext = {
  variables: FormulaVariables
  formulas?: Array<{ name: string; code: string }>
  /** Names in `variables` that reflect an EXPLICIT project value (persisted
   *  HouseElement row), not merely a seeded catalog default. When a formula
   *  library entry shares a name with an explicitly-overridden variable, the
   *  formula is skipped and the variable wins. Absent / empty set means every
   *  variable is treated as a fallback default and formulas may still run. */
  explicitlyOverriddenNames?: ReadonlySet<string>
}

/** Builds the scope a formula runs in, for the place it is evaluated at — one
 *  room, one storey, or the property as a whole. Callers that evaluate against
 *  a mounted configurator hand its factory down so the result reflects the
 *  live selections. */
export type FormulaContextFactory = (focus: SelectionScope) => FormulaContext

/** What a place contributes to a formula context before the tree's own
 *  option codes and formula library are added to it. */
export type FormulaScope = {
  variables: FormulaVariables
  explicitlyOverriddenNames: ReadonlySet<string>
}

/** Result coercion — only finite numbers (or booleans → 1/0) survive. */
function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value === 'number' && isFinite(value)) return value
  return null
}

/**
 * Scope coercion — anything assignable to a formula variable. Allows string
 * results from library sub-formulas (e.g. a lookup formula that returns an
 * OPTION key) to land in scope for downstream comparisons.
 */
function toScopeValue(value: unknown): number | string | null {
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 1 : 0
  if (typeof value === 'number' && isFinite(value)) return value
  return null
}

// Parsing is the expensive part and a formula's code is a stable string, so
// the answer is kept per code. Capped like `REFERENCES_CACHE`, and for the
// same reason: the formula editor asks about a fresh draft on every
// keystroke, and past the cap the table is dropped whole rather than growing
// for the life of the process.
const WRITES_TO_SCOPE_CACHE = new Map<string, boolean>()
const WRITES_TO_SCOPE_CACHE_MAX = 500

// The mathjs node types that cannot write to the scope they are evaluated
// against. An allow-list rather than a list of the two assignment forms, so a
// node type a later mathjs adds falls to the safe side on its own.
const READ_ONLY_NODE_TYPES = new Set([
  'AccessorNode',
  'ArrayNode',
  'BlockNode',
  'ConditionalNode',
  'ConstantNode',
  'FunctionNode',
  'IndexNode',
  'ObjectNode',
  'OperatorNode',
  'ParenthesisNode',
  'RangeNode',
  'RelationalNode',
  'SymbolNode',
])

/**
 * Whether evaluating `code` can write into the scope object it is handed.
 * `math.evaluate` mutates the scope for `x = 5`, for `livingArea = 7`, and
 * for `f(x) = x * 2` — the two assignment forms — and for nothing else.
 *
 * Unparseable code answers `true`: the evaluation will throw and be caught,
 * and an isolated scope is the safe reading of "we cannot tell".
 */
function writesToScope(code: string): boolean {
  const cached = WRITES_TO_SCOPE_CACHE.get(code)
  if (cached !== undefined) return cached
  let writes = false
  try {
    // `traverse` visits the node itself as well as its descendants; `forEach`
    // would miss a bare `x = 5`, which is the whole case.
    math.parse(code).traverse(node => {
      if (!READ_ONLY_NODE_TYPES.has(node.type)) writes = true
    })
  } catch {
    writes = true
  }
  if (WRITES_TO_SCOPE_CACHE.size >= WRITES_TO_SCOPE_CACHE_MAX) {
    WRITES_TO_SCOPE_CACHE.clear()
  }
  WRITES_TO_SCOPE_CACHE.set(code, writes)
  return writes
}

/**
 * `scope` plus a `null` placeholder for every variable a library formula
 * guards with `isset()` itself. Without the placeholder mathjs raises
 * "Undefined symbol" and the formula never resolves, defeating the guard.
 * A name that already carries a value keeps it — a resolved sibling formula
 * outranks the placeholder.
 *
 * The copy is what isolates the caller's scope, so it is skipped only when
 * there is nothing to add AND the formula cannot write. Cloning a scope of a
 * few hundred keys, once per library formula per pass, is most of what an
 * evaluation costs.
 */
function scopeWithGuardedNulls(
  scope: Record<string, number | string | null>,
  guardedNullVars: readonly string[],
  code: string,
): Record<string, number | string | null> {
  if (guardedNullVars.length === 0 && !writesToScope(code)) return scope
  const out = { ...scope }
  for (const key of guardedNullVars) {
    if (!(key in out)) out[key] = null
  }
  return out
}

/**
 * Whether an availability rule holds — `hiddenWhen`, `disabledWhen`,
 * `requiredWhen`.
 *
 * `raw: true` is the whole correctness of the gate and the reason this is a
 * function rather than four copies of one line: rounded, a result like 0.005
 * collapses to 0 and the rule silently reads false. An absent expression is
 * not a rule and never holds.
 */
export function ruleHolds(
  expression: string | null | undefined,
  context: FormulaContext,
): boolean {
  if (!expression) return false
  return Boolean(evaluateFormula(expression, context, { raw: true }))
}

/**
 * Evaluates a formula string with provided variable values and optional
 * formula library. Returns null if any referenced variable is null/undefined
 * or if evaluation fails. Strings are first-class scope values — OPTION-mode
 * variables can be compared via `==` thanks to the shared math instance's
 * equality override.
 *
 * By default the result is rounded to 2 decimals (currency-friendly display).
 * Pass `{ raw: true }` to skip the rounding — used by boolean-gate callers
 * (availability formulas) where a tiny non-zero result must not collapse to 0.
 *
 * `extra` adds names for this one expression without touching the context:
 * the staged calculation puts each stage's subtotal in scope that way. A
 * caller that cloned the context instead would get a new object identity, and
 * both memos below are weakly keyed on it — the library's fixed point would
 * then be resolved again for every expression, which is most of what an
 * evaluation costs.
 */
type ScopeValue = number | string | null

const LIBRARY_SCOPES = new WeakMap<FormulaContext, Record<string, ScopeValue>>()

/**
 * The context's variables split into the ones that carry a value and the names
 * that do not, kept per context.
 *
 * The split is a walk of every variable a project holds — every option code,
 * every house-element key — and it is the same walk for every rule evaluated
 * against that context. Weakly keyed, like the library scope beside it, and
 * sound for the same reason: a context is built once per place and never
 * written to afterwards.
 */
const SPLIT_VARIABLES = new WeakMap<
  FormulaContext,
  { values: Record<string, ScopeValue>; nullKeys: string[] }
>()

export function evaluateFormula(
  code: string,
  context: FormulaContext,
  options: { raw?: boolean; extra?: FormulaVariables } = {},
): number | null {
  try {
    // `null` is a valid scope value so `isset(x)` can distinguish present-
    // but-null from absent. mathjs would otherwise choke on `null + n`, so
    // we only leak `null` into scope for variables the author guarded with
    // at least one `isset(x)` reference — combined with the lazy `if()`
    // override in math-instance.ts, the common idiom
    //   if(isset(x), x * 100, 0)
    // never evaluates the `x * 100` branch when x is null. A truly bare
    // reference to a null variable in a non-isset formula still
    // short-circuits.
    const { values, nullKeys } = splitVariables(context)

    const mainNullVars = analyzeNullVarUsage(code, nullKeys)
    if (mainNullVars.blockingVar) return null

    // The library's scope already carries every variable that has a value, so
    // it is this expression's scope unless the expression adds to it: a
    // guarded null to place, or an assignment of its own. An availability rule
    // is neither, and that is most of what is ever evaluated.
    const base = context.formulas
      ? libraryScope(context, values, nullKeys)
      : values
    // `extra` is a handful of names the caller puts into scope for this one
    // expression. An expression naming none of them needs no copy to receive
    // them — and the staged engine hands `extra` to every term it prices, so
    // this is the difference between copying a few hundred keys per
    // expression and not copying at all.
    const readsExtra =
      options.extra != null &&
      Object.keys(options.extra).some(key =>
        formulaReferences(code).names.has(key),
      )
    const scope =
      mainNullVars.guardedNullVars.length === 0 &&
      !readsExtra &&
      !writesToScope(code)
        ? base
        : { ...base }
    for (const key of mainNullVars.guardedNullVars) {
      // Only where the library did not resolve a formula of that name: a
      // resolved sibling outranks the placeholder.
      if (!(key in base)) scope[key] = null
    }
    // Last, so a name the caller is putting into scope for this one
    // expression wins over a library formula that happens to share it.
    if (readsExtra) Object.assign(scope, options.extra!)

    const result = math.evaluate(code, scope)
    const numeric = toFiniteNumber(result)
    if (numeric === null) return null
    return options.raw ? numeric : Math.round(numeric * 100) / 100
  } catch {
    return null
  }
}

function splitVariables(context: FormulaContext): {
  values: Record<string, ScopeValue>
  nullKeys: string[]
} {
  const cached = SPLIT_VARIABLES.get(context)
  if (cached) return cached

  const values: Record<string, ScopeValue> = {}
  const nullKeys: string[] = []
  for (const [key, value] of Object.entries(context.variables)) {
    if (value == null) nullKeys.push(key)
    else values[key] = value
  }
  const split = { values, nullKeys }
  SPLIT_VARIABLES.set(context, split)
  return split
}

/**
 * What the formula library resolves to for a context, once.
 *
 * Resolving it is a fixed point over every formula in it and costs far more
 * than the one expression it is resolved for — an availability pass asks for
 * it once per rule per option and gets the same answer every time.
 *
 * The answer depends on the context alone: the library is resolved against the
 * variables that carry a value plus each formula's own `isset` guards, so what
 * an unrelated expression happened to guard cannot change it. Weakly keyed, so
 * a context that goes out of scope takes its entry with it.
 */
function libraryScope(
  context: FormulaContext,
  values: Record<string, ScopeValue>,
  nullKeys: string[],
): Record<string, ScopeValue> {
  const cached = LIBRARY_SCOPES.get(context)
  if (cached) return cached

  const resolved = resolveLibrary(
    values,
    context.formulas ?? [],
    context,
    nullKeys,
  )
  LIBRARY_SCOPES.set(context, resolved)
  return resolved
}

/**
 * The library resolved against the variables that carry a value, as a scope of
 * its own.
 *
 * Only a formula whose name was NOT explicitly overridden by a persisted
 * ElementType value is injected. Seeded catalogue defaults (a QUANTITY-mode 0)
 * do not block it — they exist only so downstream references stay defined, and
 * the formula is what the admin wanted for that "no project value yet" case.
 *
 * A multi-pass fixed point, because sub-formulas reference each other and the
 * library is not sorted by dependency: a single pass would let formula B fail
 * with "Undefined symbol A" because A appears later in the array. The
 * remaining set is retried every pass until nothing new resolves — O(depth)
 * passes for an acyclic graph, and a cycle terminates at once because no pass
 * makes progress.
 *
 * Each formula's own `isset()` guards are analysed once: the null set is fixed
 * for the whole call, so the result holds across every pass.
 *
 * `values` is read and not written: the caller's own guards are not part of
 * what a library formula may see, which is what lets one answer serve every
 * expression asked of this context.
 */
function resolveLibrary(
  values: Record<string, ScopeValue>,
  formulas: NonNullable<FormulaContext['formulas']>,
  context: FormulaContext,
  nullKeys: string[],
): Record<string, ScopeValue> {
  const scope: Record<string, ScopeValue> = { ...values }
  const overridden = context.explicitlyOverriddenNames
  let remaining = formulas
    .filter(f => !overridden?.has(f.name))
    .map(f => ({
      ...f,
      guardedNullVars: analyzeNullVarUsage(f.code, nullKeys).guardedNullVars,
    }))
  while (remaining.length > 0) {
    const stillRemaining: typeof remaining = []
    let progressed = false
    for (const formula of remaining) {
      try {
        const result = math.evaluate(
          formula.code,
          scopeWithGuardedNulls(scope, formula.guardedNullVars, formula.code),
        )
        const coerced = toScopeValue(result)
        if (coerced !== null) scope[formula.name] = coerced
        // Even a null-coerced result counts as "resolved" — the formula ran
        // cleanly, it just returned a non-numeric.
        progressed = true
      } catch {
        // Likely referencing a sibling formula not yet in scope. Retry after
        // the next pass in case that sibling resolves.
        stillRemaining.push(formula)
      }
    }
    if (!progressed) break
    remaining = stillRemaining
  }

  return scope
}

export type FormulaTraceStatus =
  | { kind: 'ok'; value: number }
  | { kind: 'syntax-error'; message: string }
  | { kind: 'runtime-error'; message: string }
  | { kind: 'non-finite'; raw: unknown }
  | {
      kind: 'null-variable'
      /** Name of the variable that short-circuited the evaluation. */
      variable: string
    }

export type FormulaSubTrace = {
  name: string
  result: number | string | null
  /** Set when the sub-formula was skipped because its name is in
   *  `explicitlyOverriddenNames` (the ElementType-over-Formula priority
   *  rule from evaluateFormula). NOT a failure — the UI must NOT list
   *  overridden entries under "Sub-Formeln die scheitern". */
  overridden?: boolean
  /** Set when the sub-formula's own code bare-references a null variable
   *  with no isset guard. Populated BEFORE mathjs would throw an
   *  "Undefined symbol" error, so the UI can attribute the failure to
   *  the source variable instead of surfacing raw mathjs jargon. */
  nullVariable?: string
  /** Populated when the sub-formula raised at math.evaluate — kept
   *  alongside `nullVariable` when both apply so debug detail (like the
   *  raw mathjs error) isn't lost. */
  error?: string
}

export type FormulaTrace = {
  result: number | null
  status: FormulaTraceStatus
  subFormulas: FormulaSubTrace[]
  /** The scope the main expression was evaluated against — merged from
   *  `context.variables` plus every sub-formula whose result coerced to
   *  a scope value. Useful to render "what values were plugged in". */
  scope: Record<string, number | string | null>
}

/**
 * Textual null-variable analysis shared by the main-code and sub-formula
 * passes. Returns:
 *   - `blockingVar`: the first null variable that IS bare-referenced AND
 *     has no isset() guard — mathjs would throw at eval; caller should
 *     short-circuit.
 *   - `guardedNullVars`: null variables that ARE isset-guarded and can
 *     safely be leaked into scope so the lazy `if` override can skip the
 *     guarded branch.
 * Returns null blockingVar when the code contains no bare null-var refs
 * that lack a guard.
 */
function analyzeNullVarUsage(
  code: string,
  // Names whose context value is null. Analysing this list rather than the
  // whole variable map keeps the per-library-formula pass proportional to the
  // handful of unset variables instead of the hundreds of option codes and
  // element keys that fill a project's scope.
  nullKeys: readonly string[],
  // Optional live-scope snapshot. When provided, a variable that is
  // null in `variables` but has been overridden by a prior sub-formula
  // (scope[key] set to a real value) is NOT treated as blocking. Without
  // this, a sub-formula whose name shadows a null variable would cause
  // downstream sub-formulas that reference that name to be falsely
  // flagged as blocked, diverging from fast-path evaluateFormula.
  scope: Record<string, number | string | null> = {},
): { blockingVar: string | null; guardedNullVars: string[] } {
  if (nullKeys.length === 0) return { blockingVar: null, guardedNullVars: [] }
  // One scan of the code rather than two patterns per null variable: the
  // scanner answers both questions at once, and it reads a quoted literal as
  // content the way the editor paints it.
  const read = formulaReferences(code)
  const guardedNullVars: string[] = []
  for (const key of nullKeys) {
    // Scope override wins over the frozen context value — a sub-formula
    // that already ran and produced a real value for this key is not
    // null anymore.
    const overriddenInScope =
      key in scope && scope[key] !== null && scope[key] !== undefined
    if (overriddenInScope) continue
    if (!read.names.has(key)) continue
    if (read.issetGuarded.has(key)) {
      guardedNullVars.push(key)
    } else {
      return { blockingVar: key, guardedNullVars }
    }
  }
  return { blockingVar: null, guardedNullVars }
}

/** Names in `variables` that carry no value. */
function collectNullKeys(variables: FormulaContext['variables']): string[] {
  const keys: string[] = []
  for (const [key, value] of Object.entries(variables)) {
    if (value == null) keys.push(key)
  }
  return keys
}

/**
 * Diagnostic variant of `evaluateFormula`. Returns the same numeric
 * result plus a `status` field that explains WHY a null-result happened
 * (syntax error / runtime error / null variable / non-finite result),
 * a `subFormulas` array capturing per-library-formula outcomes, and the
 * final `scope` the main expression actually saw. Consumers (the CMS
 * FormulaEditor) render this to help admins debug "Auf Anfrage" surfaces
 * without having to reason through the entire library chain by hand.
 *
 * Kept separate from the fast-path `evaluateFormula` so hot paths
 * (customer pricing, planning read-model) don't pay the trace overhead
 * on every render.
 */
export function evaluateFormulaWithTrace(
  code: string,
  context: FormulaContext,
  options: { raw?: boolean; extra?: FormulaVariables } = {},
): FormulaTrace {
  // The same two options the fast path takes, because a trace of an
  // expression the engine priced with `extra` has to run in the scope the
  // engine used: a staged term reading `subtotal` is evaluated against a name
  // the context does not hold, and a trace without it reports no value for a
  // term that priced correctly — the one panel built to explain a price
  // contradicting it.
  const scope: Record<string, number | string | null> = {
    ...options.extra,
  }
  const subFormulas: FormulaSubTrace[] = []
  // Belt-and-suspenders outer try/catch. The main paths below throw only
  // through math.parse/evaluate (already caught), but scope construction
  // and RegExp building could throw on pathological input — mirror
  // evaluateFormula's blanket try/catch so the trace variant never
  // crashes the DiagnosePanel above it.
  try {
    // Step 1: syntax check FIRST. When both a syntax error AND a null-
    // variable would apply, the syntax error is the stronger claim (the
    // formula is broken regardless of scope) — reporting it first also
    // avoids the double-diagnostic contradiction with the inline
    // `syntaxError` red row rendered by the editor.
    try {
      math.parse(code)
    } catch (err) {
      return {
        result: null,
        status: { kind: 'syntax-error', message: (err as Error).message },
        subFormulas,
        scope,
      }
    }

    // Step 2: null-var analysis on the MAIN expression. If a bare null-var
    // is unguarded, short-circuit exactly the way evaluateFormula does.
    const nullKeys = collectNullKeys(context.variables)
    const mainNullVars = analyzeNullVarUsage(code, nullKeys)
    for (const key of mainNullVars.guardedNullVars) {
      scope[key] = null
    }
    // Also seed non-null variables that the main formula references OR
    // any sub-formula might reference — matches evaluateFormula's
    // "always add non-null" invariant.
    for (const [key, value] of Object.entries(context.variables)) {
      if (value != null && !(key in scope)) scope[key] = value
    }
    if (mainNullVars.blockingVar) {
      return {
        result: null,
        status: {
          kind: 'null-variable',
          variable: mainNullVars.blockingVar,
        },
        subFormulas,
        scope,
      }
    }

    // Step 3: fold in the library via multi-pass fixed-point evaluation.
    // Sub-formulas can reference each other; the library isn't sorted by
    // dependency, so a formula B that references sibling formula A can
    // fail on the first pass if A appears LATER in the array. Retry the
    // unresolved set until nothing new resolves — O(depth) for acyclic
    // dependency graphs, terminates immediately on cycles.
    //
    // Per-pass: (a) null-var analysis against the CURRENT scope (so a
    // sub-formula that shadows a null variable and was resolved earlier
    // doesn't falsely block dependents). (b) mathjs evaluate; on
    // "Undefined symbol" the formula stays pending and retries next pass.
    // Any formula that never resolves ends up in the final pass and is
    // recorded with its terminal diagnostic (nullVariable or raw error).
    if (context.formulas) {
      const overridden = context.explicitlyOverriddenNames
      const resolved = new Set<string>()

      // Track final trace state per formula so we can push subFormulas
      // entries in the ORIGINAL library order at the end.
      const traceByName = new Map<string, FormulaSubTrace>()

      // Seed overridden entries — they don't participate in the loop.
      for (const formula of context.formulas) {
        if (overridden?.has(formula.name)) {
          traceByName.set(formula.name, {
            name: formula.name,
            result: (scope[formula.name] as number | string | null) ?? null,
            overridden: true,
          })
          resolved.add(formula.name)
        }
      }

      let remaining = context.formulas.filter(f => !resolved.has(f.name))
      while (remaining.length > 0) {
        const stillRemaining: typeof remaining = []
        let progressed = false
        for (const formula of remaining) {
          const subNullVars = analyzeNullVarUsage(formula.code, nullKeys, scope)
          if (subNullVars.blockingVar) {
            // Blocking var might resolve later if it's a sibling formula
            // name and that sibling gets set by a later pass. Retry.
            stillRemaining.push(formula)
            continue
          }
          try {
            const rawResult = math.evaluate(
              formula.code,
              scopeWithGuardedNulls(
                scope,
                subNullVars.guardedNullVars,
                formula.code,
              ),
            )
            const coerced = toScopeValue(rawResult)
            if (coerced !== null) scope[formula.name] = coerced
            traceByName.set(formula.name, {
              name: formula.name,
              result: coerced,
            })
            resolved.add(formula.name)
            progressed = true
          } catch {
            // Likely "Undefined symbol X" where X is a sibling formula not
            // yet resolved. Retry after another pass.
            stillRemaining.push(formula)
          }
        }
        if (!progressed) break
        remaining = stillRemaining
      }

      // Final pass: anything still remaining is permanently blocked
      // (cycle, references a var that never became defined, etc.).
      // Record the terminal reason so the DiagnosePanel has real info.
      for (const formula of remaining) {
        const subNullVars = analyzeNullVarUsage(formula.code, nullKeys, scope)
        if (subNullVars.blockingVar) {
          traceByName.set(formula.name, {
            name: formula.name,
            result: null,
            nullVariable: subNullVars.blockingVar,
          })
          continue
        }
        try {
          math.evaluate(
            formula.code,
            scopeWithGuardedNulls(
              scope,
              subNullVars.guardedNullVars,
              formula.code,
            ),
          )
          // Reached only if a prior pass's throw was environmental (rare);
          // treat as null result with no explicit error.
          traceByName.set(formula.name, { name: formula.name, result: null })
        } catch (err) {
          traceByName.set(formula.name, {
            name: formula.name,
            result: null,
            error: (err as Error).message ?? 'unknown',
          })
        }
      }

      // Push subFormulas in the ORIGINAL library order so the DiagnosePanel
      // presentation matches the admin's mental model of the library.
      for (const formula of context.formulas) {
        const entry = traceByName.get(formula.name)
        if (entry) subFormulas.push(entry)
      }
    }

    // Last, so a name the caller put into scope for this one expression wins
    // over a library formula that happens to share it — the same precedence
    // `evaluateFormula` applies.
    if (options.extra) Object.assign(scope, options.extra)

    // Step 4: evaluate the main expression against the built scope.
    let raw: unknown
    try {
      raw = math.evaluate(code, scope)
    } catch (err) {
      return {
        result: null,
        status: { kind: 'runtime-error', message: (err as Error).message },
        subFormulas,
        scope,
      }
    }

    const numeric = toFiniteNumber(raw)
    if (numeric === null) {
      return {
        result: null,
        status: { kind: 'non-finite', raw },
        subFormulas,
        scope,
      }
    }
    const rounded = options.raw ? numeric : Math.round(numeric * 100) / 100
    return {
      result: rounded,
      status: { kind: 'ok', value: rounded },
      subFormulas,
      scope,
    }
  } catch (err) {
    return {
      result: null,
      status: {
        kind: 'runtime-error',
        message: (err as Error).message ?? 'unknown internal error',
      },
      subFormulas,
      scope,
    }
  }
}

/**
 * Validates a formula string for syntax errors.
 * Returns null if valid, or an error message string.
 */
export function validateFormula(code: string): string | null {
  try {
    math.parse(code)
    return null
  } catch (e: any) {
    return e.message || 'Invalid formula syntax'
  }
}

/**
 * What is wrong with an expression a field holds, as a control's `error`.
 *
 * An empty field is not yet an expression, so it says nothing — and the
 * message is `undefined` rather than null, because that is what a control
 * reads as "no error".
 */
export const formulaError = (code: string | null | undefined) =>
  code?.trim() ? (validateFormula(code) ?? undefined) : undefined

/**
 * Detects circular references in a formula library.
 * Returns the cycle path if detected, or null if no cycle.
 */
export function detectCircularFormulas(
  formulas: Array<{ name: string; code: string }>,
  newFormulaName: string,
  newFormulaCode: string,
): string[] | null {
  const formulaMap = new Map<string, string>()
  for (const f of formulas) {
    formulaMap.set(f.name, f.code)
  }
  formulaMap.set(newFormulaName, newFormulaCode)

  const allNames = new Set(formulaMap.keys())

  function getReferencedFormulas(code: string): string[] {
    // One scan, and the same answer the usage count and the delete guard
    // give: a name inside a quoted literal is content, not a reference.
    const read = formulaNames(code)
    return [...allNames].filter(name => read.has(name))
  }

  function dfs(
    name: string,
    path: string[],
    visited: Set<string>,
  ): string[] | null {
    if (visited.has(name)) {
      return [...path, name]
    }
    visited.add(name)

    const code = formulaMap.get(name)
    if (!code) return null

    for (const ref of getReferencedFormulas(code)) {
      const cycle = dfs(ref, [...path, name], new Set(visited))
      if (cycle) return cycle
    }

    return null
  }

  return dfs(newFormulaName, [], new Set())
}
